import React, {useEffect, useMemo} from "react";
import {useForm, Controller, useWatch} from "react-hook-form";

import {useSelector} from "react-redux";
import {FaseFrazione, saveFaseFrazione} from "./frazioniOdPSlice";
import {RootState, useAppDispatch} from "../../../redux/store";
import {Operatore} from "../personale/operatoriSlice";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { NumericFormat } from "react-number-format";

type FaseFrazioneFormValues = FaseFrazione & { mantieniOreManuali: boolean };

const TOOLTIP_ORE_PREVISTE =
    "Quando modifichi la quantità, le ore vengono ricalcolate dalla proporzione del ciclo eSolver " +
    "(ore_previste × quantità / quantità_fase + attrezzaggio). " +
    "Se l'OdP è già in produzione e le ore lavorate eSolver non riflettono ancora le quantità prodotte " +
    "(eSolver versa le quantità solo a chiusura fase), attiva \"Mantieni ore manuali\" per evitare il ricalcolo.";

// ============================================
// INTERFACES
// ============================================

interface FaseFrazioneEditModalProps {
    open: boolean;
    faseFrazione: FaseFrazione | undefined;
    onClose: () => void;
    onSaveSuccess: () => void;
}

// ============================================
// COMPONENT
// ============================================

const FaseFrazioneEditModal = ({
    open,
    faseFrazione,
    onClose,
    onSaveSuccess,
}: FaseFrazioneEditModalProps) => {
    const dispatch = useAppDispatch();

    const operatori = useSelector<RootState, Operatore[]>(
        (state) => state.operatoriState?.operatoriList?.results || []
    );

    // Operatori del reparto della fase + operatore corrente (anche se di reparto
    // diverso, per non farlo sparire da assegnazioni storiche incongruenti).
    // Se la fase non ha reparto, mostra tutti gli attivi (il BE valida pass-through).
    const operatoriDisponibili = useMemo(() => {
        const attivi = operatori.filter((op) => op.attivo);
        if (!faseFrazione?.reparto) return attivi;
        return attivi.filter(
            (op) => op.reparto === faseFrazione.reparto || op.id === faseFrazione.operatore
        );
    }, [operatori, faseFrazione?.reparto, faseFrazione?.operatore]);

    const {control, handleSubmit, reset, formState: {isDirty}} = useForm<FaseFrazioneFormValues>({
        defaultValues: {
            quantita: faseFrazione?.quantita ?? 0,
            ore_previste: faseFrazione?.ore_previste ?? 0,
            operatore: faseFrazione?.operatore ?? undefined,
            data_inizio_pianificata: faseFrazione?.data_inizio_pianificata ?? "",
            data_fine_pianificata: faseFrazione?.data_fine_pianificata ?? "",
            note: faseFrazione?.note ?? "",
            mantieniOreManuali: false,
        },
    });

    // Reset form quando cambia la fase
    useEffect(() => {
        if (faseFrazione) {
            reset({
                quantita: faseFrazione.quantita ?? 0,
                ore_previste: faseFrazione.ore_previste ?? 0,
                operatore: faseFrazione.operatore ?? undefined,
                data_inizio_pianificata: faseFrazione.data_inizio_pianificata ?? "",
                data_fine_pianificata: faseFrazione.data_fine_pianificata ?? "",
                note: faseFrazione.note ?? "",
                mantieniOreManuali: false,
            });
        }
    }, [faseFrazione, reset]);

    // Watch live per pilotare l'helper text del campo ore_previste
    const quantitaWatched = useWatch({control, name: "quantita"});
    const mantieniOreWatched = useWatch({control, name: "mantieniOreManuali"});
    const quantitaIniziale = Number(faseFrazione?.quantita ?? 0);
    const quantitaModificata = Number(quantitaWatched ?? 0) !== quantitaIniziale;

    let oreHelperText = "";
    let oreHelperColor: "warning.main" | "info.main" | undefined;
    if (quantitaModificata && !mantieniOreWatched) {
        oreHelperText = "Le ore verranno ricalcolate al salvataggio";
        oreHelperColor = "warning.main";
    } else if (quantitaModificata && mantieniOreWatched) {
        oreHelperText = "Ore manuali preservate; date ricalcolate sulle ore inserite";
        oreHelperColor = "info.main";
    }

    const onSubmit = async (data: FaseFrazioneFormValues) => {
        if (!faseFrazione?.id || !faseFrazione?.frazione) return;

        const {mantieniOreManuali, ...formData} = data;
        const faseDaSalvare = {
            ...faseFrazione,
            ...formData,
            mantieni_ore: mantieniOreManuali,
        } as FaseFrazione;

        const risultato = await dispatch(
            saveFaseFrazione({
                frazioneId: faseFrazione.frazione,
                faseFrazioneToSave: faseDaSalvare,
            })
        );

        if (risultato.meta.requestStatus === "fulfilled") {
            onSaveSuccess();
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Modifica Fase
                {faseFrazione && (
                    <Typography variant="body2" color="text.secondary">
                        F{faseFrazione.ordinamento} - {faseFrazione.codice_lavorazione} | {faseFrazione.descrizione}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{mt: 1}}>
                    <Stack direction="row" spacing={2}>
                    <Controller
                        name="quantita"
                        control={control}
                        render={({field}) => (
                            <TextField
                                {...field}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    field.onChange(val === "" ? "" : Math.trunc(Number(val)));
                                }}
                                label="Quantit&agrave;"
                                type="number"
                                size="small"
                                fullWidth
                                slotProps={{
                                    inputLabel: {shrink: true},
                                    htmlInput: {step: 1},
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "." || e.key === ",") {
                                        e.preventDefault();
                                    }
                                }}
                            />
                        )}
                    />
                    <Controller
                        name="ore_previste"
                        control={control}
                        render={({field}) => (
                            <NumericFormat
                                customInput={TextField}
                                label="Ore previste"
                                size="small"
                                fullWidth
                                thousandSeparator="."
                                decimalSeparator=","
                                decimalScale={4}
                                value={field.value}
                                onValueChange={(values) => {
                                    field.onChange(values.floatValue ?? "");
                                }}
                                helperText={oreHelperText || undefined}
                                slotProps={{
                                    inputLabel: {shrink: true},
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Tooltip title={TOOLTIP_ORE_PREVISTE} arrow>
                                                    <InfoOutlinedIcon
                                                        sx={{fontSize: 16, color: "text.secondary", cursor: "help"}}
                                                    />
                                                </Tooltip>
                                            </InputAdornment>
                                        ),
                                    },
                                    formHelperText: oreHelperColor
                                        ? {sx: {color: oreHelperColor}}
                                        : undefined,
                                }}
                            />
                        )}
                    />
                    </Stack>

                    <Controller
                        name="mantieniOreManuali"
                        control={control}
                        render={({field}) => (
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={!!field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        Mantieni ore manuali
                                        <Typography
                                            component="span"
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{display: "block", lineHeight: 1.2}}
                                        >
                                            Salta il ricalcolo proporzionale delle ore quando modifichi la quantità
                                        </Typography>
                                    </Typography>
                                }
                                sx={{alignItems: "flex-start", mt: -0.5}}
                            />
                        )}
                    />

                    <Stack direction="row" spacing={2}>
                        <Controller
                            name="data_inizio_pianificata"
                            control={control}
                            render={({field}) => (
                                <TextField
                                    {...field}
                                    label="Data inizio pianificata"
                                    type="date"
                                    size="small"
                                    fullWidth
                                    slotProps={{inputLabel: {shrink: true}}}
                                />
                            )}
                        />

                        <Controller
                            name="data_fine_pianificata"
                            control={control}
                            render={({field}) => (
                                <TextField
                                    {...field}
                                    label="Data fine pianificata"
                                    type="date"
                                    size="small"
                                    fullWidth
                                    slotProps={{inputLabel: {shrink: true}}}
                                />
                            )}
                        />
                    </Stack>

                    <Controller
                        name="operatore"
                        control={control}
                        render={({field}) => (
                            <TextField
                                {...field}
                                select
                                label="Operatore"
                                size="small"
                                fullWidth
                                value={field.value ?? ""}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    field.onChange(val === "" ? null : Number(val));
                                }}
                                slotProps={{inputLabel: {shrink: true}}}
                            >
                                <MenuItem value="">
                                    <em>Nessuno</em>
                                </MenuItem>
                                {operatoriDisponibili.map((op) => (
                                    <MenuItem key={op.id} value={op.id}>
                                        {op.codice} - {op.nominativo}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    />

                    <Controller
                        name="note"
                        control={control}
                        render={({field}) => (
                            <TextField
                                {...field}
                                label="Note"
                                multiline
                                minRows={5}
                                size="small"
                                fullWidth
                                slotProps={{inputLabel: {shrink: true}}}
                            />
                        )}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Annulla
                </Button>
                <Button
                    onClick={handleSubmit(onSubmit)}
                    variant="contained"
                    disabled={!isDirty}
                >
                    Salva
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FaseFrazioneEditModal;
